import { useCallback, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useMountRead } from "@/hooks/useMountRead";
import {
    Title,
    Button,
    Menu,
    Spinner,
    ErrorState,
    type MenuItem,
} from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import type { CategoryResponse } from "@models/category";
import {
    categoriesAtom,
    isFetchingCategoriesAtom,
} from "@/store/categoryAtoms";
import { NewCategory } from "./new-category";
import { EditCategory } from "./edit-category";
import { DeleteCategoryDialog } from "./_components/DeleteCategoryDialog";

interface EditTarget {
    category: CategoryResponse;
    // The tree node carries no parentId; it is captured during the walk so
    // the (full-replace) update can send it back unchanged.
    parentId: number | null;
}

export const AdminCategories = () => {
    const { getCategories } = useCategoryApi();
    const categories = useAtomValue(categoriesAtom);
    const isFetching = useAtomValue(isFetchingCategoriesAtom);
    const [editing, setEditing] = useState<EditTarget | null>(null);
    const [deleting, setDeleting] = useState<CategoryResponse | null>(null);
    // Where the delete confirm returns focus once the deleted category's row
    // (its opener) is gone, instead of <body>.
    const headingRef = useRef<HTMLHeadingElement>(null);

    // Issued on mount. The page shows a failure only for its own read: the
    // app-scoped error flag has six writers across the app (#407). Wrapped so
    // the `isCurrent` useMountRead hands a read never reaches getCategories as
    // its options.
    const readCategories = useCallback(() => getCategories(), [getCategories]);
    const { readStatus, reload } = useMountRead(readCategories);

    const convertCategoriesToMenuItems = (
        categories: CategoryResponse[],
        parentId: number | null = null,
    ): MenuItem[] => {
        return categories.map((category) => ({
            id: category.id,
            label: category.name,
            onClick: () => setEditing({ category, parentId }),
            actionLabel: "Edit",
            children: category.children
                ? convertCategoriesToMenuItems(category.children, category.id)
                : undefined,
        }));
    };

    const menuItems = convertCategoriesToMenuItems(categories);

    return (
        <div className="px-10">
            <div className="grid grid-cols-12 gap-4">
                <Title headingRef={headingRef}>Manage Categories</Title>
                <div className="col-span-1 mt-1">
                    <Button
                        color={ComponentColor.Secondary}
                        size={ComponentSize.SM}
                        onClick={() => {
                            const modal = document.getElementById(
                                "newCategoryModal",
                            ) as HTMLDialogElement;
                            modal?.showModal();
                        }}
                    >
                        + New
                    </Button>
                </div>
            </div>
            {readStatus === "failed" && categories.length === 0 ? (
                <ErrorState onRetry={reload} />
            ) : isFetching || readStatus === "loading" ? (
                <Spinner size="lg" />
            ) : categories.length > 0 ? (
                <Menu
                    items={menuItems}
                    size={ComponentSize.SM}
                    defaultExpanded={false}
                    showExpandToggle={true}
                    className="w-full"
                />
            ) : (
                <div className="text-center text-neutral-content p-8">
                    <p>{"No categories found."}</p>
                </div>
            )}
            <NewCategory />
            {editing && (
                <EditCategory
                    key={editing.category.id}
                    category={editing.category}
                    parentId={editing.parentId}
                    onClose={() => setEditing(null)}
                    onDelete={() => setDeleting(editing.category)}
                />
            )}
            {deleting && (
                <DeleteCategoryDialog
                    key={deleting.id}
                    category={deleting}
                    onClose={() => setDeleting(null)}
                    returnFocusRef={headingRef}
                />
            )}
        </div>
    );
};
